/**
 * Pure supports for the session view — extracted at the 500-line cap (D121)
 * exactly as `use-ambience-driver.ts` and `export-transcript.ts` were before
 * them: functions with no component state, moved whole, so `session-view.tsx`
 * keeps only the composition. D156 moved the plate and the chips into the
 * host's projection; what remains is the shell's own: the viewport.
 *
 * @packageDocumentation
 */

import type { CommandSuggestion, CueView, ResolvedLine, SessionView } from '@orpheus/api-contracts';
import type { RuntimeTranscriptEntry } from '@orpheus/engine-runtime';

import { planTranscriptReveal } from '@orpheus/player-ui';

/**
 * D103 (Mike's finding): pin the viewport as a turn lands — but never past
 * unread prose. The old pin snapped to the BOTTOM, so a turn taller than the
 * viewport jumped its own beginning off screen — worst exactly where re-visit
 * instantness (D99's rule) lands a large block at once. Now a turn taller
 * than the viewport lands with its START at the top (the prompt lives OUTSIDE
 * this scroll region, so nothing is hidden by reading from the top), and a
 * shorter turn scrolls to the bottom as before — smoothly, unless the player
 * asked for reduced motion, in which case the scroll is a cut like every
 * other animation in the shell.
 *
 * @param viewport - The transcript scroll region
 */
export const pinViewportToNewestTurn = (viewport: HTMLDivElement): void => {
	const newestTurn = viewport.querySelector<HTMLElement>('.transcript-turn:last-of-type');
	// matchMedia is guarded for jsdom, which ships neither it nor scrollTo (an
	// environment with no media queries also runs no smooth scrolling — the
	// scrollTop fallback below is a cut regardless).
	const isReducedMotionPreferred = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const behavior: ScrollBehavior = isReducedMotionPreferred ? 'auto' : 'smooth';
	const turnTop =
		newestTurn === null
			? undefined
			: newestTurn.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop;
	const scrollTarget =
		turnTop !== undefined && newestTurn !== null && newestTurn.offsetHeight > viewport.clientHeight
			? turnTop
			: viewport.scrollHeight;

	// jsdom ships no scrollTo; the assignment fallback keeps the suites honest.
	if (typeof viewport.scrollTo === 'function') {
		viewport.scrollTo({ behavior, top: scrollTarget });
	} else {
		viewport.scrollTop = scrollTarget;
	}
};

/** The pieces of the view the session component reads directly, with their absences settled once. */
export interface ViewReading {
	readonly activeRoomKey: string | undefined;
	/** The room's bed — or quiet once the session has ended: a death is not scored by the room the player died in. */
	readonly ambience: SessionView['ambience'];
	readonly pacing: SessionView['pacing'];
	/** The room's picture, under the Images toggle: absent when the room shows none or the player switched it off. */
	readonly scene: SessionView['scene'];
	/** The pack's declared voices (D192) — none until a session is open, and none for a pack with no speakers. */
	readonly speakers: SessionView['speakers'];
	readonly suggestions: readonly CommandSuggestion[];
	/** How many entries the transcript holds — one more for every answered input, a refusal as much as a turn. */
	readonly transcriptLength: number | undefined;
}

/**
 * Read the view for rendering — every optional settled here, so the
 * component's own branching stays about presentation.
 *
 * @param view - The view the host last handed the store, if a session is open
 * @param isRoomImageryEnabled - The player's Images toggle
 * @returns The reading
 */
export const readView = (view: SessionView | undefined, isRoomImageryEnabled: boolean): ViewReading => ({
	activeRoomKey: view?.activeRoomKey,
	// The room's bed still resolves under an ending (the player is still standing somewhere), but the
	// Meridian's vacuum deaths end in silence ("and then the room is only the lamp's"), and so does every
	// ending: the bed fades, and the restart's room brings its own back in.
	ambience: view?.isEnded === true ? 'silence' : view?.ambience,
	pacing: view?.pacing,
	scene: isRoomImageryEnabled ? view?.scene : undefined,
	speakers: view?.speakers ?? [],
	suggestions: view?.pendingQuestion?.suggestions ?? [],
	transcriptLength: view?.transcript.length,
});

/** D165: a turn the edge of the demo refused — what the player typed, and the game's words for the wall. */
export interface WallAnswer {
	readonly input: string;
	readonly lines: readonly ResolvedLine[];
}

const INPUT_ORDINAL = /^input-(?<ordinal>\d+)-/u;

/**
 * The transcript with the wall's answer as its newest turn: the player's words as the heading, the game's lines beneath.
 *
 * @remarks
 * A refused turn is not in the record (D165: the same words go through once the game is bought), so the host's view
 * ends before it. The wall's lines used to stand in a notice under the transcript, outside the scroll region, and at
 * a desktop height a notice of four lines and two buttons took all of the room the transcript had: the history could
 * not be scrolled, and the words the player typed were nowhere (Mike, 2026-11-02). They are the turn's answer, so they
 * are drawn as one — under the next input's ordinal, which is how the list groups a turn — until the next turn answers.
 *
 * @param entries - The view's transcript
 * @param wall - The refused turn, when the last one was
 * @param turnCount - The view's turn count, stamped on the drawn entries
 * @returns The entries to draw
 */
export const withWallAnswer = (entries: readonly RuntimeTranscriptEntry[], wall: WallAnswer | undefined, turnCount: number): readonly RuntimeTranscriptEntry[] => {
	if (wall === undefined) {
		return entries;
	}

	const ordinal = 1 + Math.max(0, ...entries.map(({ id }) => Number(INPUT_ORDINAL.exec(id)?.groups?.['ordinal'] ?? 0)));
	const echo: RuntimeTranscriptEntry[] =
		wall.input.trim() === '' ? [] : [{ id: `input-${String(ordinal)}-0`, priority: 'standard', source: 'player', text: wall.input, triggerContext: 'player_input', turnCount }];

	return [
		...entries,
		...echo,
		...wall.lines.map((line, index) => ({ id: `input-${String(ordinal)}-${String(index + 1)}`, priority: line.priority, source: line.source, text: line.text, turnCount })),
	];
};

/**
 * D265: when each of a turn's cues should sound — the moment the reveal begins the line that says it, after the
 * curtain the plate's room change holds the prose behind.
 *
 * @remarks
 * The times are the list's own: the same plan (`planTranscriptReveal`) over the same entries, boundary and voices, so
 * a sound and its line begin together. A line the reveal does not perform (its exact text was said before, and a
 * re-visit is consulted, not experienced) still says its sound now: it sounds where the line before it began. A cue
 * that names no entry (the wall's lines are drawn, not recorded) sounds as the turn arrives. The plan is made on the
 * first cue asked about, so the renders that heard nothing never make it.
 *
 * @param entries - The transcript as drawn, oldest first
 * @param revealFromIndex - The reveal boundary
 * @param speakers - The pack's voices, for a speaker line's pace
 * @param baseDelayMs - The curtain the prose waits out, in milliseconds
 * @returns Each cue's start, in milliseconds from now
 */
export const cueTimingOf = (
	entries: readonly RuntimeTranscriptEntry[],
	revealFromIndex: number,
	speakers: SessionView['speakers'],
	baseDelayMs: number,
): ((cue: CueView) => number) => {
	let startsById: ReadonlyMap<string, number> | undefined;

	const lineStarts = (): ReadonlyMap<string, number> => {
		const { revealStartDelaysMsById } = planTranscriptReveal(entries, revealFromIndex, speakers);
		const starts = new Map<string, number>();
		let latestStartMs = 0;

		for (const entry of entries.slice(revealFromIndex)) {
			latestStartMs = revealStartDelaysMsById.get(entry.id) ?? latestStartMs;
			starts.set(entry.id, latestStartMs);
		}

		return starts;
	};

	return ({ entryId }) => {
		startsById ??= lineStarts();

		return baseDelayMs + (entryId === undefined ? 0 : (startsById.get(entryId) ?? 0));
	};
};
